import { AntDesign } from "@expo/vector-icons";
import { withObservables } from "@nozbe/watermelondb/react";
import { Banner, Divider, Page, Text } from "@towersystems/roam-common-native";
import { useCallback, useEffect } from "react";
import { FlatList, View } from "react-native";
import colors from "react-native-ui-lib/src/style/colors";
import { compose } from "recompose";
import { map } from "rxjs/operators";
import { StockCountListItem } from "../../components";
import { Database, Q } from "@nozbe/watermelondb";
import { withDatabase } from "@nozbe/watermelondb/DatabaseProvider";
import { useIsFocused, useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import { StockCountStatus } from "@towersystems/roam-common/lib/generated-types";
import { Spacings } from "react-native-ui-lib";
import { StockCountsRootParamList } from "../../../../types";
import StockCount from "../../../../utilities/data/model/StockCount";

export interface StockCountsScreenOfflineProps {
  stockCounts: StockCount[];
}

type StockCountsScreenOfflineNavigationType = NativeStackNavigationProp<
  StockCountsRootParamList,
  "StockCountsScreenOffline"
>;

function StockCountsScreenOfflineInner({
  stockCounts,
}: StockCountsScreenOfflineProps) {
  const navigation = useNavigation<StockCountsScreenOfflineNavigationType>();

  const isOnline = useOnline();
  const isFocused = useIsFocused();

  const renderItem = useCallback(({ item }: { item: StockCount }) => {
    return (
      <StockCountListItem
        item={item as any}
        onPress={() =>
          // @ts-ignore
          navigation.navigate("PerformStockCountRootScreen", {
            screen: "StockCountScreen",
            stockCountId: item.id,
          })
        }
      />
    );
  }, []);

  useEffect(() => {
    if (isOnline && isFocused) {
      navigation.navigate("StockCountsScreen");
    }
  }, [isOnline, isFocused]);

  return (
    <Page
      title="Offline stock counts"
      secondaryTitle="You can continue stock counts that were started while you were online, even if you lose connection. Just tap on a stock count to continue."
    >
      <View
        style={{
          marginBottom: Spacings.s4,
        }}
      >
        <Banner
          title="Viewing offline stock counts"
          status="info"
          icon={
            <AntDesign name="infocirlce" size={20} color={colors.borderInfo} />
          }
        >
          <Text>
            Continue an existing stock count offline. You will be able to sync
            it later.
          </Text>
        </Banner>
      </View>
      <FlatList
        ItemSeparatorComponent={() => <Divider />}
        renderItem={renderItem}
        ListEmptyComponent={<Text>No pending stock counts to show</Text>}
        data={stockCounts.map((item) => item as any)}
      ></FlatList>
    </Page>
  );
}

const getStockCountsObservables = ({ database }: { database: Database }) => {
  const stockCounts = database.collections
    .get<StockCount>("stock_counts")
    .query(
      Q.where("status", Q.notIn([StockCountStatus.COMPLETE])),
      Q.where("deleted_at", Q.eq(null)),
    )
    .observe();

  return {
    stockCounts: stockCounts.pipe(
      map((results: StockCount[]) => results.map((item: StockCount) => item)),
    ),
  };
};

const enhance = compose<any, any>(
  withDatabase,
  withObservables([], getStockCountsObservables),
);

export const StockCountsScreenOffline = enhance(StockCountsScreenOfflineInner);
